import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from './supabase';

// Format score with + or - sign
const formatScore = (score) => {
  if (score === 0 || score === 'E') return 'E';
  if (typeof score === 'string') return score;
  return score > 0 ? `+${score}` : `${score}`;
};

// Get Tailwind CSS class for score text color
const getScoreColorClass = (score) => {
  if (score === undefined || score === null) return '';
  const num = typeof score === 'string' ? parseInt(score.replace('+', '')) : score;
  if (num < 0) return 'text-green-500';
  if (num > 0) return 'text-red-500';
  return '';
};


const getResultType = (score, par) => {
  if (score === null || par === null) return 'par';
  const diff = score - par;
  if (diff <= -3) return 'albatross';
  if (diff === -2) return 'eagle';
  if (diff === -1) return 'birdie';
  if (diff === 0) return 'par';
  if (diff === 1) return 'bogey';
  if (diff === 2) return 'double-bogey';
  return 'x-over-par';
};

// Get Tailwind CSS class for result indicator (left border)
const getResultBorderClass = (result) => {
  switch (result) {
    case 'albatross':
      return 'border-l-indigo-500';
    case 'eagle':
      return 'border-l-blue-500';
    case 'birdie':
      return 'border-l-green-500';
    case 'par':
      return 'border-l-gray-500';
    case 'bogey':
      return 'border-l-yellow-500';
    case 'double-bogey':
      return 'border-l-orange-500';
    case 'x-over-par':
      return 'border-l-red-500';
    default:
      return 'border-l-gray-500';
  }
};

// Component for a single update card
const UpdateCard = ({ update, index }) => {
  const { 
    player_name, 
    hole_num, 
    hole_par, 
    hole_score, 
    round_num, 
    tournament_score, 
    tournament_position, 
    round_score, 
    thru, 
    created_at 
  } = update;
  
  // State to force re-render for time updates
  const [currentTime, setCurrentTime] = useState(new Date());
  
  // Update current time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute
    
    return () => clearInterval(timer);
  }, []);
  
  // Format time relative to current time
  const timeAgo = useMemo(() => {
    const date = new Date(created_at);
    const diffInSeconds = Math.floor((currentTime - date) / 1000);
    
    if (diffInSeconds < 60) return 'just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    return `${Math.floor(diffInSeconds / 86400)}d ago`;
  }, [created_at, currentTime]);

  const result = getResultType(hole_score, hole_par);
    // Build hole description based on the result classification
  const diff = hole_score - hole_par;
  const holeOrdinal = `${hole_num}${['st', 'nd', 'rd'][hole_num - 1] || 'th'}`;

  const description = (() => {
    switch (result) {
      case 'albatross':
        return `makes an albatross on the Par ${hole_par} ${holeOrdinal}`;
      case 'eagle':
        return `eagles the Par ${hole_par} ${holeOrdinal}`;
      case 'birdie':
        return `birdies the Par ${hole_par} ${holeOrdinal}`;
      case 'par':
        return `pars the Par ${hole_par} ${holeOrdinal}`;
      case 'bogey':
        return `bogeys the Par ${hole_par} ${holeOrdinal}`;
      case 'double-bogey':
        return `double bogeys the Par ${hole_par} ${holeOrdinal}`;
      case 'x-over-par':
        return `records +${diff} on the Par ${hole_par} ${holeOrdinal}`;
      default:
        return `plays the Par ${hole_par} ${holeOrdinal}`;
    }
  })();
  
  // Helper function to add ordinal suffix
  const getOrdinalSuffix = (num) => {
    const j = num % 10;
    const k = num % 100;
    if (j === 1 && k !== 11) return num + 'st';
    if (j === 2 && k !== 12) return num + 'nd';
    if (j === 3 && k !== 13) return num + 'rd';
    return num + 'th';
  };

  // Determine the place text
  const getPlaceText = () => {
    if (!tournament_position) return '';
    const standing = tournament_position.replace(/[^0-9T]/g, '');
    
    // Return empty string if no numbers found in position
    if (!/\d/.test(standing)) return '';
    
    if (standing === '1') return '1st';
    if (standing === '2') return '2nd';
    if (standing === '3') return '3rd';
    if (standing.includes('T')) {
      const pos = parseInt(standing.replace('T', ''), 10);
      return `T${getOrdinalSuffix(pos)}`;
    }
    return getOrdinalSuffix(parseInt(standing, 10));
  };

  const placeText = getPlaceText();
  const isTied = tournament_position && tournament_position.includes('T');
  const fullDescription = placeText && result !== 'par'
    ? isTied 
      ? `${description}. He is now tied for ${placeText.replace('T', '')} place.`
      : `${description}. He is now in ${placeText} place.`
    : `${description}.`;

  return (
    <div className="relative mt-2">
      <div 
        className={`relative overflow-visible bg-gray-800/80 rounded-lg p-4 sm:p-5 border-l-4 ${getResultBorderClass(result)} border-gray-700/50 transition-all duration-200 ease-out hover:bg-gray-800/90 hover:shadow-lg hover:-translate-y-0.5 animate-fadeIn`}
        style={{ animationDelay: `${index * 100}ms` }}
      >
        {/* Round badge */}
        <div className="absolute -top-2 -right-2 text-xs py-1 px-2.5 bg-blue-500/20 text-blue-300 rounded-2xl font-medium border border-blue-500/30 backdrop-blur-sm z-10">
          R{round_num}
        </div>
      
        {/* Description with inline timestamp */}
        <div className="text-[15px] leading-normal text-gray-200 pl-1 mb-3">
          <div>
            <strong className="font-semibold text-white">{player_name}</strong> {fullDescription}
            <span className="text-xs text-gray-500 ml-2">{timeAgo}</span>
          </div>
        </div>
        
        {/* Stats */}
        <div className="flex flex-wrap gap-2 text-sm pl-1">
          <div className="flex flex-col items-center py-1.5 px-2.5 rounded-lg bg-gray-700/50 border border-gray-600/30 min-w-[70px]">
            <span className="text-xs text-gray-400 mb-0.5 uppercase tracking-wider font-medium">SCORE</span>
            <span className={`font-semibold text-base ${
              getScoreColorClass(parseInt(tournament_score || 0))
            }`}>
              {formatScore(tournament_score)}
            </span>
          </div>
          
          {tournament_position && (
            <div className="flex flex-col items-center py-1.5 px-2.5 rounded-lg bg-gray-700/50 border border-gray-600/30 min-w-[70px]">
              <span className="text-xs text-gray-400 mb-0.5 uppercase tracking-wider font-medium">STANDING</span>
              <span className="font-semibold text-gray-100 text-base">
                {tournament_position}
              </span>
            </div>
          )}
          
          {round_score && (
            <div className="flex flex-col items-center py-1.5 px-2.5 rounded-lg bg-gray-700/50 border border-gray-600/30 min-w-[70px]">
              <span className="text-xs text-gray-400 mb-0.5 uppercase tracking-wider font-medium">TODAY</span>
              <span className={`font-semibold text-base ${
                getScoreColorClass(parseInt(round_score || 0))
              }`}>
                {formatScore(round_score)}
              </span>
            </div>
          )}
          
          {thru && (
            <div className="flex flex-col items-center py-1.5 px-2.5 rounded-lg bg-gray-700/50 border border-gray-600/30 min-w-[70px]">
              <span className="text-xs text-gray-400 mb-0.5 uppercase tracking-wider font-medium">THRU</span>
              <span className="font-semibold text-gray-100 text-base">{thru}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

function App() {
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Set dark background for the entire app
  useEffect(() => {
    document.body.className = 'bg-gray-900 min-h-screen text-gray-100';
    return () => {
      document.body.className = '';
    };
  }, []);

  useEffect(() => {
    // Initial fetch
    const fetchInitialUpdates = async () => {
      const { data, error } = await supabase
        .from('golf_hole_results')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50);

      if (!error && data) {
        setUpdates(data);
      }
      setLoading(false);
    };

    fetchInitialUpdates();

    // Set up real-time subscription
    const subscription = supabase
      .channel('golf_updates')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'golf_hole_results',
      }, (payload) => {
        setUpdates(prevUpdates => {
          const newUpdates = [payload.new, ...prevUpdates];
          // Keep only the most recent 50 updates
          return newUpdates.slice(0, 50);
        });
      })
      .subscribe();

    // Cleanup subscription on unmount
    return () => {
      subscription.unsubscribe();
    };
  }, []);

  return (
    <div className="max-w-3xl mx-auto py-8 px-4 sm:px-5">
      <header className="mb-8">
        <h1 className="text-[28px] font-bold flex items-center tracking-[-0.5px]">
          <span className="mr-3 text-[28px] relative z-[2]">
            ⛳
            <div className="absolute w-10 h-10 bg-gradient-to-tr from-blue-500/70 to-blue-400/30 to-65% rounded-full -z-[1] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 blur-lg"></div>
          </span>
          <span className="bg-gradient-to-r from-blue-400 to-blue-300 bg-clip-text text-transparent">GolfTracker Live</span>
        </h1>
      </header>
      
      {loading ? (
        <div className="text-center py-10 text-gray-400">Loading updates...</div>
      ) : updates.length === 0 ? (
        <div className="text-center py-10 text-gray-400">No updates available</div>
      ) : (
        <div className="flex flex-col gap-2">
          {updates.map((update, index) => (
            <UpdateCard key={`${update.id}-${update.created_at}`} update={update} index={index} />
          ))}
        </div>
      )}
    </div>
  );
}

export default App;
